// Human-in-the-loop gate for destructive/supervised tool calls. The agent's
// workflow is parked on a Temporal signal while this card is undecided;
// Approve/Reject files the decision (POST /approvals/{id}/decide) which
// resumes it. If nobody decides within the server's approval window the
// request auto-rejects, so an abandoned card can't strand the turn.

import { useEffect, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Button, Text } from '@kula-ai/components';
import { decideApproval } from '../../../api/approvals';
import {
  APPROVAL_RECORD_FAILED,
  APPROVAL_UNDO_WARNING,
} from '../../../constants';
import type { ApprovalBlockData } from '../../../types';

interface ApprovalBlockProps {
  data: ApprovalBlockData;
}

// "kula_contact_delete" → "contact delete" — readable without a label table.
const humanizeTool = (tool: string): string =>
  tool.replace(/^kula_/, '').replaceAll('_', ' ');

type DecisionState =
  | 'pending'
  | 'deciding'
  | 'approved'
  | 'rejected'
  | 'failed'
  | 'expired';

// mm:ss for the auto-decline countdown.
const formatCountdown = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Seconds until the server auto-rejects, ticking once a second while the
// card is undecided. Returns null when no deadline was shipped (older
// backend) or before the first tick — clock reads happen only inside the
// interval callback (render stays pure per the repo lint).
const useCountdown = (
  expiresAt: number | undefined,
  active: boolean
): number | null => {
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (!expiresAt || !active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt, active]);
  if (!expiresAt || now === 0) return null;
  return Math.max(0, Math.floor(expiresAt - now / 1000));
};

export const ApprovalBlock = ({ data }: ApprovalBlockProps) => {
  const [state, setState] = useState<DecisionState>('pending');

  const undecided = state === 'pending' || state === 'deciding';
  const remaining = useCountdown(data.expiresAt, undecided);
  // The server auto-rejects at the deadline; flip the card so it never
  // shows live Approve/Reject buttons for a decision that can no longer
  // be filed. Derived during render — no effect needed.
  const expired = undecided && remaining !== null && remaining <= 0;
  const effectiveState: DecisionState = expired ? 'expired' : state;

  const decide = (approved: boolean) => {
    if (state === 'deciding' || expired) return;
    setState('deciding');
    decideApproval(data.recordId, approved)
      .then(() => setState(approved ? 'approved' : 'rejected'))
      .catch(() => setState('failed'));
  };

  const decided =
    effectiveState === 'approved' ||
    effectiveState === 'rejected' ||
    effectiveState === 'expired';

  return (
    <Box
      data-test-id="agentforge-approval-block"
      border="1px solid"
      borderColor={decided ? 'secondary-200' : 'warning-300'}
      borderRadius="12px"
      bg={decided ? 'secondary-25' : 'warning-25'}
      px="16px"
      py="12px"
      maxW="520px"
    >
      <Text variant="span" className="text-secondary-900 font-semibold">
        Approval needed: {humanizeTool(data.toolName)}
      </Text>
      {data.reason ? (
        <Text variant="small" as="p" className="text-secondary-600 mt-1">
          {data.reason}
        </Text>
      ) : null}
      <Text variant="small" as="p" className="text-secondary-500 mt-1">
        {APPROVAL_UNDO_WARNING}
      </Text>

      <Flex mt="10px" gap="8px" align="center">
        {effectiveState === 'pending' || effectiveState === 'deciding' ? (
          <>
            <Button
              variant="primary"
              size="sm"
              onClick={() => decide(true)}
              isDisabled={state === 'deciding'}
              data-test-id="agentforge-approval-approve"
            >
              Approve
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => decide(false)}
              isDisabled={state === 'deciding'}
              data-test-id="agentforge-approval-reject"
            >
              Reject
            </Button>
            {remaining !== null && remaining > 0 && (
              <Text
                variant="small"
                className="text-secondary-500"
                data-test-id="agentforge-approval-countdown"
              >
                Auto-declines in {formatCountdown(remaining)}
              </Text>
            )}
          </>
        ) : effectiveState === 'expired' ? (
          <Text
            variant="small"
            className="text-secondary-600"
            data-test-id="agentforge-approval-expired"
          >
            Expired — this request was auto-declined. Send your message again to
            retry.
          </Text>
        ) : state === 'failed' ? (
          <Text variant="small" className="text-error-600">
            {APPROVAL_RECORD_FAILED}
          </Text>
        ) : (
          <Text variant="small" className="text-secondary-600 font-medium">
            {state === 'approved'
              ? '✓ Approved — running now.'
              : '✕ Rejected — the agent will skip it.'}
          </Text>
        )}
        {state === 'failed' ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setState('pending')}
          >
            Retry
          </Button>
        ) : null}
      </Flex>
    </Box>
  );
};
